/**
 * Device information detection utilities for FCM token registration
 */

interface DeviceInfo {
  device_type: 'web';
  device_model: string;
  device_os_version: string;
  device_info: string;
  app_version: string;
}

/**
 * Get browser name and version
 */
const getBrowserInfo = (): { name: string; version: string } => {
  const userAgent = navigator.userAgent;

  // Chrome
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
    return {
      name: 'Chrome',
      version: match ? match[1] : 'Unknown'
    };
  }

  // Edge
  if (userAgent.includes('Edg')) {
    const match = userAgent.match(/Edg\/(\d+\.\d+)/);
    return {
      name: 'Edge',
      version: match ? match[1] : 'Unknown'
    };
  }

  // Firefox
  if (userAgent.includes('Firefox')) {
    const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
    return {
      name: 'Firefox',
      version: match ? match[1] : 'Unknown'
    };
  }

  // Safari
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    const match = userAgent.match(/Version\/(\d+\.\d+)/);
    return {
      name: 'Safari',
      version: match ? match[1] : 'Unknown'
    };
  }

  // Opera
  if (userAgent.includes('OPR')) {
    const match = userAgent.match(/OPR\/(\d+\.\d+)/);
    return {
      name: 'Opera',
      version: match ? match[1] : 'Unknown'
    };
  }

  return {
    name: 'Unknown Browser',
    version: 'Unknown'
  };
};

/**
 * Get operating system information
 */
const getOSInfo = (): { name: string; version: string } => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;

  // Windows
  if (userAgent.includes('Windows NT')) {
    const match = userAgent.match(/Windows NT (\d+\.\d+)/);
    const version = match ? match[1] : 'Unknown';
    return {
      name: 'Windows',
      version: version
    };
  }

  // macOS
  if (userAgent.includes('Mac OS X') || platform.includes('Mac')) {
    const match = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
    const version = match ? match[1].replace(/_/g, '.') : 'Unknown';
    return {
      name: 'macOS',
      version: version
    };
  }

  // Linux
  if (userAgent.includes('Linux') || platform.includes('Linux')) {
    return {
      name: 'Linux',
      version: 'Unknown'
    };
  }

  // iOS (mobile Safari)
  if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
    const match = userAgent.match(/OS (\d+[._]\d+[._]?\d*)/);
    const version = match ? match[1].replace(/_/g, '.') : 'Unknown';
    return {
      name: 'iOS',
      version: version
    };
  }

  // Android
  if (userAgent.includes('Android')) {
    const match = userAgent.match(/Android (\d+\.\d+)/);
    const version = match ? match[1] : 'Unknown';
    return {
      name: 'Android',
      version: version
    };
  }

  return {
    name: 'Unknown OS',
    version: 'Unknown'
  };
};

/**
 * Get app version from package.json
 */
const getAppVersion = (): string => {
  // In production builds, you might want to inject this via build process
  // For now, we'll use a default version
  return process.env.REACT_APP_VERSION || '1.0.0';
};

/**
 * Generate complete device information for FCM token registration
 */
export const getDeviceInfo = (): DeviceInfo => {
  const browser = getBrowserInfo();
  const os = getOSInfo();
  const appVersion = getAppVersion();

  return {
    device_type: 'web',
    device_model: `${browser.name} Browser`,
    device_os_version: `${browser.name} ${browser.version}`,
    device_info: `${os.name} ${os.version}`,
    app_version: appVersion
  };
};

/**
 * Get a human-readable device description
 */
export const getDeviceDescription = (): string => {
  const browser = getBrowserInfo();
  const os = getOSInfo();

  return `${browser.name} ${browser.version} on ${os.name} ${os.version}`;
};

/**
 * Check if device supports push notifications
 */
export const isDeviceSupported = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
};
